import { useEffect, useState } from 'react';
import { Alert, Box, Card, CardContent } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useAnalytics } from '../../../../../api/analytics';
import CustomCircularProgress from '../../../../../components/ui/customCircularProgress/customCircularProgress.component';
import { useBreadcrumbs } from '../../../../../components/ui/breadcrumps/breadcrumbsContext';
import { useHasPermission } from '../../../../../contexts/auth';
import { EquipmentFullServiceStats } from '../../../../../types/analytics';
import DateRangeFilter from '../../../../../components/ui/dateRangeFilter/dateRangeFilter.component';
import EquipmentServiceStatsContent from '../../../../../components/service/equipmentServiceStatsContent/equipmentServiceStatsContent.component';
import { useDateRangeQueryParams } from '../../../../../hooks/useDateRangeQueryParams';

export const EquipmentServiceStats = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { setOverride, clearOverride } = useBreadcrumbs();
  const canViewJobs = useHasPermission('service.view_job');
  const { loading, error, getEquipmentFullServiceStats } = useAnalytics();
  const { dateRange, setDateRange } = useDateRangeQueryParams();
  const [stats, setStats] = useState<EquipmentFullServiceStats | null>(null);

  useEffect(() => {
    if (!id) {
      return undefined;
    }

    const statsPath = `/equipment-section/equipments/${id}/service-stats`;
    setOverride(statsPath, t('Service Stats'));

    return () => {
      clearOverride(statsPath);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, t]);

  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      if (!id || !canViewJobs) {
        return;
      }
      const data = await getEquipmentFullServiceStats(id, dateRange);
      if (!cancelled) {
        setStats(data || null);
      }
    };

    fetchStats();
    return () => {
      cancelled = true;
    };
  }, [id, dateRange, canViewJobs, getEquipmentFullServiceStats]);

  if (!canViewJobs) {
    return null;
  }

  if (loading && !stats) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CustomCircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{t('There was an error, please review the form.')}</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 1 }}>
      <Card>
        <CardContent>
          <DateRangeFilter value={dateRange} onChange={setDateRange} />
        </CardContent>
      </Card>

      {!stats || !id ? (
        <Alert severity="info">{t('No data available')}</Alert>
      ) : (
        <EquipmentServiceStatsContent equipmentId={id} stats={stats} dateRange={dateRange} />
      )}
    </Box>
  );
};
